import api from '@js/actions/api';

import I18n from '@js/modules/translations';
import AnalyticsService from '@js/modules/analyticsService';
import PromptBudget from '@js/modules/promptBudget';

import {basketStore} from '@js/stores/basketStore';

import {trackBanditExposure, trackExperimentExposure, trackLimitReached} from '@js/actions/trackingActions';
import Orchestrator from '@js/modules/orchestrator';

import Notification from '@js/components/layouts/notification';
import {paywallTier, Products, resolveMaxDistanceMeters, MILE_TO_METERS} from '@js/components/modules/constants';
import {rideMaxDurationHours} from '@js/modules/ride/rideLimitErrors';
import {userStore} from '@js/stores/userStore';
import {mapStore} from '@js/stores/mapStore';
import {productPath} from '@js/constants/routesHelper';


export const addToBasket = (productId, origin) => (
    api.post('/api/v1/baskets', {
        basket: {
            products: [{
                productId
            }],
            origin
        }
    })
);

export const fetchProduct = (productId, options = {}, requestOptions = {}) => (
    api.get(`/api/v1/products/${productId}`, {
        ...options
    }, requestOptions)
);

// Creates the Stripe subscription of the current basket when the customer confirms (deferred intent).
export const createStripeSubscription = (basketId) => (
    api.post('/api/v1/stripe-subscriptions', {
        basketId
    })
);

export const chargeBasket = (basketId, paymentData, options = {}) => (
    api.post('/api/v1/orders', {
        basketId: basketId,
        order: paymentData,
        ...options
    })
);

// Cap the modal PER functionality (not globally): a user who exhausted the upsell for one
// premium feature should still get it when they hit a *different* premium feature.
const PRODUCT_MODAL_SESSION_KEY = 'product_modal_shown_counts';
const PRODUCT_MODAL_MAX_SHOWS = 2;

const SOFT_NUDGE_SESSION_KEY = 'product_soft_nudge_shown_count';
const SOFT_NUDGE_MAX_SHOWS = 2;
// The A/B surface soft_nudge_cap (config/experiments.yml) may set another cap for this visitor.
const _softNudgeMaxShows = () => Number(window.experimentParams?.soft_nudge_cap) || SOFT_NUDGE_MAX_SHOWS;

// Counts that couldn't be saved (storage full or blocked) live in the page: storageKey → {functionality: count}.
const pageCounts = {};

// unreadable: the count to assume when storage can't be read.
const _countFor = (storageKey, functionality, unreadable = 0) => {
    try {
        const counts = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
        return Math.max(counts[functionality] || 0, pageCounts[storageKey]?.[functionality] || 0);
    } catch {
        return Math.max(unreadable, pageCounts[storageKey]?.[functionality] || 0);
    }
};

// inPage: remember the count for the page when storage can't save it.
const _incrementCountFor = (storageKey, functionality, inPage = false) => {
    const count = _countFor(storageKey, functionality) + 1;
    try {
        const counts = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
        counts[functionality] = count;
        window.sessionStorage.setItem(storageKey, JSON.stringify(counts));
    } catch {
        if (inPage) {
            pageCounts[storageKey] = {...pageCounts[storageKey], [functionality]: count};
        }
    }
};

const _formatDistanceLimit = () => {
    const unitKey = userStore.unit === 'mi' ? 'mi' : 'km';
    // Limit for the activity that triggered the nudge (the one being drawn).
    const meters = resolveMaxDistanceMeters(userStore.featureFlags.map_max_distance, mapStore.currentRideType, userStore.unit) || 0;
    const value = Math.round(unitKey === 'mi' ? meters / MILE_TO_METERS : meters / 1000);

    return `${value}${I18n.t(`js.metrics.${unitKey}.distance_unit_k`)}`;
};

const _limitMessage = (requiredFunctionality) => {
    const params = { defaultValue: I18n.t('js.product.modal.soft_nudge') };

    if (requiredFunctionality === 'map_max_distance') {
        params.distance = _formatDistanceLimit();
        params.activity = I18n.t(`js.ride.enums.ride_type.${mapStore.currentRideType}`);
    }

    if (requiredFunctionality === 'ride_max_duration') {
        params.hours = rideMaxDurationHours(userStore.featureFlags.ride_max_duration);
    }

    return I18n.t(`js.product.features.${requiredFunctionality}.limit`, params);
};

// withCta: a hard limit turned into this notice (paywall_by_intent) keeps a way to buy.
const _showSoftNudge = (requiredFunctionality, {withCta = false} = {}) => {
    // The cap decides here: a visitor in a running experiment is exposed to it (a soft limit only).
    if (window.experiments?.soft_nudge_cap && !withCta) {
        trackExperimentExposure('soft_nudge_cap');
    }
    // No storage, no nudge: a count that can't be read would never reach the cap.
    if (_countFor(SOFT_NUDGE_SESSION_KEY, requiredFunctionality, Infinity) >= (withCta ? SOFT_NUDGE_MAX_SHOWS : _softNudgeMaxShows())) {
        // A downgraded hard limit past the cap still gets the toast and its Explorer link.
        if (withCta) {
            _showLimitToast(requiredFunctionality);
        }
        return;
    }
    _incrementCountFor(SOFT_NUDGE_SESSION_KEY, requiredFunctionality, true);

    AnalyticsService.trackPaywallSoftNudge(requiredFunctionality);
    Notification.info(withCta ? `${_limitMessage(requiredFunctionality)} ${_explorerCta(requiredFunctionality, 'js.product.modal.soft_nudge_cta')}` : _limitMessage(requiredFunctionality));
};

// The quick subscribe link of a toast (managers/basketManager.js handles data-quick-subscribe).
const _explorerCta = (requiredFunctionality, labelKey) => (
    `<a href="${productPath(Products.EXPLORER)}" data-quick-subscribe="${Products.EXPLORER}" data-quick-subscribe-origin="${requiredFunctionality}" class="ml-2 font-semibold underline whitespace-nowrap">${I18n.t(labelKey)}</a>`
);

let _lastLimitToastAt = 0;
const _showLimitToast = (requiredFunctionality) => {
    const now = Date.now();
    if (now - _lastLimitToastAt < 4_000) {
        return;
    }
    _lastLimitToastAt = now;

    // Reported per toast shown, not per hit: a dragged route hits the limit on every recomputation.
    AnalyticsService.trackProductModalSuppressed(requiredFunctionality);

    Notification.info(`${_limitMessage(requiredFunctionality)} ${_explorerCta(requiredFunctionality, 'js.product.modal.btn')}`);
};

// A subscriber has nothing left to buy (and no #product-modal): the cap is stated, no paywall counted.
const _showSubscriberLimit = (requiredFunctionality) => {
    const message = requiredFunctionality === 'map_max_distance'
        ? I18n.t('js.product.modal.subscriber_distance_limit', {distance: _formatDistanceLimit()})
        : I18n.t('js.product.modal.subscriber_limit');

    Notification.info(message);
};

// A/B surface paywall_by_intent: from the live intent of the visit (modules/orchestrator.js, Intent::Live)
// and the bounds served with it (the top 10 % and the bottom 30 % of the champion's basket scores), a soft
// limit opens the Explorer window for a likely buyer, a hard one becomes the soft notice for an unlikely
// one. Without a score or bounds yet, or for the interventions' holdout, the static tier.
const _intentBandFor = (basket, bounds) => {
    if (basket >= bounds.high) {
        return 'high';
    }

    return basket <= bounds.low ? 'low' : 'middle';
};

// {tier, downgraded}: downgraded when a hard limit became the soft notice. Exposed (both groups) only where
// the treatment changes the tier: a soft limit in the high band, a hard one in the low band.
const _tierFor = (requiredFunctionality) => {
    const tier = paywallTier(requiredFunctionality);
    const intent = Orchestrator.active() ? Orchestrator.intent() : null;
    const basket = intent?.basket;
    const bounds = intent?.bounds?.basket;
    if (typeof basket !== 'number' || typeof bounds?.high !== 'number' || typeof bounds?.low !== 'number'
        || requiredFunctionality === 'product_page') {
        return {tier};
    }

    const band = _intentBandFor(basket, bounds);
    const upgraded = tier === 'soft' && band === 'high';
    if (!upgraded && !(tier === 'hard' && band === 'low')) {
        return {tier};
    }
    if (window.experiments?.paywall_by_intent) {
        trackExperimentExposure('paywall_by_intent', {tier: band, score: basket});
    }
    if (window.experimentParams?.paywall_by_intent !== true) {
        return {tier};
    }

    return upgraded ? {tier: 'hard'} : {tier: 'soft', downgraded: true};
};

export const showProductModal = (requiredFunctionality) => {
    if (['explorer', 'superuser'].includes(window.currentUserRole)) {
        _showSubscriberLimit(requiredFunctionality);
        return;
    }

    // Intent-tiered: a low-intent functionality gets a non-blocking nudge, not the modal.
    const {tier, downgraded} = _tierFor(requiredFunctionality);
    if (tier === 'soft') {
        _showSoftNudge(requiredFunctionality, {withCta: downgraded});
        return;
    }

    if (_countFor(PRODUCT_MODAL_SESSION_KEY, requiredFunctionality) >= PRODUCT_MODAL_MAX_SHOWS) {
        _showLimitToast(requiredFunctionality);
        return;
    }

    // The paywall itself is never hidden for lack of storage: only its cap falls back to the page.
    _incrementCountFor(PRODUCT_MODAL_SESSION_KEY, requiredFunctionality, true);

    AnalyticsService.trackProductModalShow(requiredFunctionality);

    basketStore.productRequiredFunctionality = requiredFunctionality;

    basketStore.offerAvailable = false;
    basketStore.usage = null;

    // For the distance limit, report the current activity so conversions split per activity.
    const limitActivity = requiredFunctionality === 'map_max_distance' ? mapStore.currentRideType : null;

    trackLimitReached(requiredFunctionality, limitActivity)
        .then((response) => {
            basketStore.offerAvailable = Boolean(response && response.offer_available);
            basketStore.usage = response && response.usage ? response.usage : null;
        })
        .catch(() => {
            basketStore.offerAvailable = false;
            basketStore.usage = null;
        });

    if (basketStore.productModal && typeof basketStore.productModal.show === 'function') {
        // The modal carries the message: a toast still on screen would only duplicate it.
        Notification.dismissForModal();
        // The subtitle decides here (ProductModal, A/B surface paywall_copy): a visitor who sees it is exposed.
        if (window.experiments?.paywall_copy && requiredFunctionality !== 'product_page') {
            trackExperimentExposure('paywall_copy');
        }
        // Or the bandit of the copies (Bandits), which records the arm and its share.
        if (window.bandits?.paywall_copy_bandit && requiredFunctionality !== 'product_page') {
            trackBanditExposure('paywall_copy_bandit');
        }
        // Never refused (conversion first): it takes the place of a lower prompt on screen.
        PromptBudget.claim('paywall', {isOpen: () => basketStore.productModal?.isVisible?.() ?? false});
        basketStore.productModal.show();
    }
};

export const hideProductModal = () => {
    basketStore.productRequiredFunctionality = undefined;
    if (basketStore.productModal && typeof basketStore.productModal.hide === 'function') {
        basketStore.productModal.hide();
    }
};
